import { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  createConference,
  getConferenceByCode,
  joinConference,
  leaveConference,
  endConference,
  updateParticipantMedia,
  admitFromWaitingRoom,
  denyFromWaitingRoom,
  removeParticipant,
} from "../../functions/call";
import {
  VideoIcon,
  MicrophoneIcon,
  MuteMicIcon,
  CameraOffIcon,
  ScreenShareIcon,
  EndCallIcon,
  GroupIcon,
  CopyIcon,
} from "../../svg/chat";
import Header from "../../components/header";
import "./style.css";

export default function ConferencePage({ socket }) {
  const { user } = useSelector((state) => state.user);
  const { code } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams(); // eslint-disable-line no-unused-vars

  // Conference state
  const [conference, setConference] = useState(null);
  const [isHost, setIsHost] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inWaitingRoom, setInWaitingRoom] = useState(false);

  // UI state
  const [showJoinModal, setShowJoinModal] = useState(!code);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const [showWaitingRoom, setShowWaitingRoom] = useState(false);
  const [showSettings, setShowSettings] = useState(false); // eslint-disable-line no-unused-vars
  const [joinCode, setJoinCode] = useState(code || "");
  const [conferenceTitle, setConferenceTitle] = useState("");
  const [callDuration, setCallDuration] = useState(0);
  const [copied, setCopied] = useState(false);

  // Media state
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [localStream, setLocalStream] = useState(null);
  const [participants, setParticipants] = useState([]); // eslint-disable-line no-unused-vars

  // Refs
  const localVideoRef = useRef(null);
  const peersRef = useRef({});
  const screenStreamRef = useRef(null);

  // Initialize media
  const initializeMedia = useCallback(async (videoEnabled = true) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: videoEnabled,
        audio: true,
      });
      setLocalStream(stream);
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
      return stream;
    } catch (error) {
      console.error("Error accessing media:", error);
      setError("Erro ao acessar camera/microfone. Verifique as permissoes.");
      return null;
    }
  }, []);

  // Create new conference
  const handleCreateConference = useCallback(async () => {
    const stream = await initializeMedia(true);
    if (!stream) return;

    setIsLoading(true);
    const result = await createConference(
      conferenceTitle || "Minha Conferencia",
      "video",
      {},
      user.token
    );

    if (result.status === "ok") {
      setConference(result.conference);
      setIsHost(true);
      setShowCreateModal(false);
      setShowJoinModal(false);
      navigate(`/conference/${result.conference.conferenceCode}`, { replace: true });
    } else {
      setError(result.message);
    }
    setIsLoading(false);
  }, [conferenceTitle, user.token, initializeMedia, navigate]);

  // Join existing conference
  const handleJoinConference = useCallback(async () => {
    if (!joinCode.trim()) return;

    const stream = await initializeMedia(true);
    if (!stream) return;

    setIsLoading(true);
    const result = await joinConference(joinCode.toUpperCase(), user.token);

    if (result.status === "ok") {
      if (result.inWaitingRoom) {
        setInWaitingRoom(true);
        setShowJoinModal(false);
      } else {
        setConference(result.conference);
        setIsHost(result.conference.caller._id === user.id);
        setShowJoinModal(false);
        navigate(`/conference/${result.conference.conferenceCode}`, { replace: true });
      }
    } else {
      setError(result.message);
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    }
    setIsLoading(false);
  }, [joinCode, user.token, user.id, initializeMedia, navigate]);

  // Leave conference
  const handleLeaveConference = useCallback(async () => {
    if (conference) {
      await leaveConference(conference._id, user.token);
    }

    // Stop all media
    if (localStream) {
      localStream.getTracks().forEach((track) => track.stop());
    }
    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach((track) => track.stop());
    }

    // Close all peer connections
    Object.values(peersRef.current).forEach((peer) => {
      if (peer) peer.destroy();
    });
    peersRef.current = {};

    navigate("/chat");
  }, [conference, user.token, localStream, navigate]);

  // End conference (host only)
  const handleEndConference = useCallback(async () => {
    if (!isHost || !conference) return;

    await endConference(conference._id, user.token);
    handleLeaveConference();
  }, [isHost, conference, user.token, handleLeaveConference]);

  // Toggle audio
  const toggleAudio = useCallback(async () => {
    if (localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsAudioEnabled(audioTrack.enabled);

        if (conference) {
          await updateParticipantMedia(
            conference._id,
            { isAudioEnabled: audioTrack.enabled },
            user.token
          );
        }
      }
    }
  }, [localStream, conference, user.token]);

  // Toggle video
  const toggleVideo = useCallback(async () => {
    if (localStream) {
      const videoTrack = localStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoEnabled(videoTrack.enabled);

        if (conference) {
          await updateParticipantMedia(
            conference._id,
            { isVideoEnabled: videoTrack.enabled },
            user.token
          );
        }
      }
    }
  }, [localStream, conference, user.token]);

  // Start screen sharing
  const startScreenShare = useCallback(async () => {
    try {
      const screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: false,
      });

      screenStreamRef.current = screenStream;
      setIsScreenSharing(true);

      const screenTrack = screenStream.getVideoTracks()[0];
      screenTrack.onended = () => stopScreenShare();

      // Replace video track in all peer connections
      Object.values(peersRef.current).forEach((peer) => {
        if (peer && peer._pc) {
          const sender = peer._pc.getSenders().find((s) => s.track?.kind === "video");
          if (sender) {
            sender.replaceTrack(screenTrack);
          }
        }
      });

      if (localVideoRef.current) {
        localVideoRef.current.srcObject = screenStream;
      }

      if (conference) {
        await updateParticipantMedia(
          conference._id,
          { isScreenSharing: true },
          user.token
        );
      }
    } catch (error) {
      console.error("Error sharing screen:", error);
    }
  }, [conference, user.token]);

  // Stop screen sharing
  const stopScreenShare = useCallback(async () => {
    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach((track) => track.stop());
      screenStreamRef.current = null;
    }

    setIsScreenSharing(false);

    // Restore video track
    if (localStream) {
      const videoTrack = localStream.getVideoTracks()[0];
      Object.values(peersRef.current).forEach((peer) => {
        if (peer && peer._pc) {
          const sender = peer._pc.getSenders().find((s) => s.track?.kind === "video");
          if (sender && videoTrack) {
            sender.replaceTrack(videoTrack);
          }
        }
      });

      if (localVideoRef.current) {
        localVideoRef.current.srcObject = localStream;
      }
    }

    if (conference) {
      await updateParticipantMedia(
        conference._id,
        { isScreenSharing: false },
        user.token
      );
    }
  }, [localStream, conference, user.token]);

  // Copy conference link
  const copyLink = useCallback(() => {
    if (conference) {
      const link = `${window.location.origin}/conference/${conference.conferenceCode}`;
      navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [conference]);

  // Admit from waiting room
  const handleAdmit = useCallback(async (visitorId) => {
    if (!isHost || !conference) return;
    await admitFromWaitingRoom(conference._id, visitorId, user.token);
  }, [isHost, conference, user.token]);

  // Deny from waiting room
  const handleDeny = useCallback(async (visitorId) => {
    if (!isHost || !conference) return;
    await denyFromWaitingRoom(conference._id, visitorId, user.token);
  }, [isHost, conference, user.token]);

  // Remove participant
  const handleRemoveParticipant = useCallback(async (participantId) => {
    if (!isHost || !conference) return;
    await removeParticipant(conference._id, participantId, user.token);
  }, [isHost, conference, user.token]);

  // Load conference if code is provided
  useEffect(() => {
    if (code && !conference) {
      const loadConference = async () => {
        setIsLoading(true);
        const result = await getConferenceByCode(code, user.token);
        if (result.status === "ok") {
          setConference(result.conference);
          setIsHost(result.conference.caller._id === user.id);
          await initializeMedia(true);
        } else {
          setError(result.message);
        }
        setIsLoading(false);
      };
      loadConference();
    } else if (!code) {
      setIsLoading(false);
    }
  }, [code, user.token, user.id, initializeMedia, conference]);

  // Call duration timer
  useEffect(() => {
    if (!conference || conference.status !== "ongoing") return;

    const interval = setInterval(() => {
      setCallDuration((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [conference]);

  // Socket event handlers
  useEffect(() => {
    if (!socket || !conference) return;

    socket.on("conference:userJoined", (data) => {
      console.log("User joined:", data);
      // Refresh conference data
      getConferenceByCode(conference.conferenceCode, user.token).then((result) => {
        if (result.status === "ok") {
          setConference(result.conference);
        }
      });
    });

    socket.on("conference:userLeft", (data) => {
      console.log("User left:", data);
      if (data.conferenceEnded) {
        handleLeaveConference();
      } else {
        getConferenceByCode(conference.conferenceCode, user.token).then((result) => {
          if (result.status === "ok") {
            setConference(result.conference);
          }
        });
      }
    });

    socket.on("conference:ended", () => {
      handleLeaveConference();
    });

    socket.on("conference:removed", () => {
      alert("Voce foi removido da conferencia");
      handleLeaveConference();
    });

    socket.on("conference:admitted", async (data) => {
      setInWaitingRoom(false);
      const result = await getConferenceByCode(data.conferenceCode, user.token);
      if (result.status === "ok") {
        setConference(result.conference);
        setIsHost(result.conference.caller._id === user.id);
      }
    });

    socket.on("conference:denied", () => {
      alert("Sua entrada foi negada");
      if (localStream) {
        localStream.getTracks().forEach((track) => track.stop());
      }
      navigate("/chat");
    });

    socket.on("conference:waitingRoom", (data) => {
      // Refresh waiting room
      getConferenceByCode(conference.conferenceCode, user.token).then((result) => {
        if (result.status === "ok") {
          setConference(result.conference);
        }
      });
    });

    socket.on("conference:mediaUpdate", (data) => {
      setParticipants((prev) =>
        prev.map((p) =>
          p.id === data.userId
            ? {
                ...p,
                isAudioEnabled: data.isAudioEnabled,
                isVideoEnabled: data.isVideoEnabled,
                isScreenSharing: data.isScreenSharing,
              }
            : p
        )
      );
    });

    return () => {
      socket.off("conference:userJoined");
      socket.off("conference:userLeft");
      socket.off("conference:ended");
      socket.off("conference:removed");
      socket.off("conference:admitted");
      socket.off("conference:denied");
      socket.off("conference:waitingRoom");
      socket.off("conference:mediaUpdate");
    };
  }, [socket, conference, user.token, user.id, handleLeaveConference, localStream, navigate]);

  // Format duration
  const formatDuration = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    }
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Get active participants
  const activeParticipants = conference?.participants?.filter(
    (p) => p.status === "answered"
  ) || [];

  // Waiting room users
  const waitingRoomUsers = conference?.waitingRoom || [];

  // Join/Create modal
  if (showJoinModal || showCreateModal) {
    return (
      <div className="conference_page">
        <Header page="conference" />
        <div className="conference_join_container">
          <div className="conference_join_modal">
            <h2>{showCreateModal ? "Criar Conferencia" : "Entrar em Conferencia"}</h2>

            {error && <div className="conference_error">{error}</div>}

            {showCreateModal ? (
              <>
                <div className="input_group">
                  <label>Nome da conferencia</label>
                  <input
                    type="text"
                    placeholder="Minha Conferencia"
                    value={conferenceTitle}
                    onChange={(e) => setConferenceTitle(e.target.value)}
                  />
                </div>
                <button
                  className="conference_btn primary"
                  onClick={handleCreateConference}
                  disabled={isLoading}
                >
                  {isLoading ? "Criando..." : "Criar Conferencia"}
                </button>
                <button
                  className="conference_btn secondary"
                  onClick={() => {
                    setShowCreateModal(false);
                    setShowJoinModal(true);
                  }}
                >
                  Entrar com codigo
                </button>
              </>
            ) : (
              <>
                <div className="input_group">
                  <label>Codigo da conferencia</label>
                  <input
                    type="text"
                    placeholder="XXX-XXX-XXX"
                    value={joinCode}
                    onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                    maxLength={11}
                  />
                </div>
                <button
                  className="conference_btn primary"
                  onClick={handleJoinConference}
                  disabled={isLoading || !joinCode.trim()}
                >
                  {isLoading ? "Entrando..." : "Entrar"}
                </button>
                <button
                  className="conference_btn secondary"
                  onClick={() => {
                    setShowJoinModal(false);
                    setShowCreateModal(true);
                  }}
                >
                  Criar nova conferencia
                </button>
              </>
            )}

            <button
              className="conference_btn cancel"
              onClick={() => navigate("/chat")}
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Waiting room screen
  if (inWaitingRoom) {
    return (
      <div className="conference_page">
        <div className="waiting_room_screen">
          <div className="waiting_room_content">
            <div className="spinner large"></div>
            <h2>Aguardando aprovacao...</h2>
            <p>O organizador ira admiti-lo em breve</p>
            <button
              className="conference_btn cancel"
              onClick={() => {
                if (localStream) {
                  localStream.getTracks().forEach((track) => track.stop());
                }
                navigate("/chat");
              }}
            >
              Sair da sala de espera
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Loading screen
  if (isLoading) {
    return (
      <div className="conference_page">
        <div className="conference_loading">
          <div className="spinner large"></div>
          <p>Carregando...</p>
        </div>
      </div>
    );
  }

  // Main conference screen
  return (
    <div className="conference_page">
      {/* Conference header */}
      <div className="conference_header">
        <div className="conference_info">
          <h3>{conference?.conferenceTitle || "Conferencia"}</h3>
          <span className="conference_code">{conference?.conferenceCode}</span>
          <span className="conference_duration">{formatDuration(callDuration)}</span>
        </div>
        <div className="conference_header_actions">
          <button className="header_action_btn" onClick={copyLink}>
            <CopyIcon color="#fff" />
            {copied ? "Copiado!" : "Copiar link"}
          </button>
          <button
            className="header_action_btn"
            onClick={() => setShowParticipants(!showParticipants)}
          >
            <GroupIcon color="#fff" />
            {activeParticipants.length}
          </button>
          {isHost && waitingRoomUsers.length > 0 && (
            <button
              className="header_action_btn waiting_room_btn"
              onClick={() => setShowWaitingRoom(!showWaitingRoom)}
            >
              Sala de espera ({waitingRoomUsers.length})
            </button>
          )}
        </div>
      </div>

      {/* Video grid */}
      <div className="conference_video_grid">
        {/* Local video */}
        <div className="video_tile local">
          <video ref={localVideoRef} autoPlay muted playsInline />
          {!isVideoEnabled && (
            <div className="video_off_overlay">
              <CameraOffIcon color="#fff" />
              <span>Camera desligada</span>
            </div>
          )}
          <div className="video_tile_info">
            <span>Voce {isHost && "(Host)"}</span>
            {!isAudioEnabled && <MuteMicIcon color="#fff" />}
          </div>
        </div>

        {/* Remote participants */}
        {activeParticipants
          .filter((p) => p.user._id !== user.id)
          .map((participant) => (
            <div key={participant.user._id} className="video_tile">
              <video autoPlay playsInline id={`video-${participant.user._id}`} />
              {!participant.isVideoEnabled && (
                <div className="video_off_overlay">
                  <img
                    src={participant.user.picture || "/images/default_user.png"}
                    alt=""
                  />
                </div>
              )}
              <div className="video_tile_info">
                <span>
                  {participant.user.first_name} {participant.user.last_name}
                </span>
                {!participant.isAudioEnabled && <MuteMicIcon color="#fff" />}
                {participant.isScreenSharing && (
                  <ScreenShareIcon color="#25d366" />
                )}
              </div>
            </div>
          ))}
      </div>

      {/* Controls */}
      <div className="conference_controls">
        <button
          className={`control_btn ${!isAudioEnabled ? "off" : ""}`}
          onClick={toggleAudio}
        >
          {isAudioEnabled ? (
            <MicrophoneIcon color="#fff" />
          ) : (
            <MuteMicIcon color="#fff" />
          )}
          <span>{isAudioEnabled ? "Mudo" : "Ativar"}</span>
        </button>

        <button
          className={`control_btn ${!isVideoEnabled ? "off" : ""}`}
          onClick={toggleVideo}
        >
          {isVideoEnabled ? (
            <VideoIcon color="#fff" />
          ) : (
            <CameraOffIcon color="#fff" />
          )}
          <span>{isVideoEnabled ? "Camera" : "Ligar"}</span>
        </button>

        <button
          className={`control_btn ${isScreenSharing ? "active" : ""}`}
          onClick={isScreenSharing ? stopScreenShare : startScreenShare}
        >
          <ScreenShareIcon color="#fff" />
          <span>{isScreenSharing ? "Parar" : "Tela"}</span>
        </button>

        <button className="control_btn end_call" onClick={isHost ? handleEndConference : handleLeaveConference}>
          <EndCallIcon color="#fff" />
          <span>{isHost ? "Encerrar" : "Sair"}</span>
        </button>
      </div>

      {/* Participants panel */}
      {showParticipants && (
        <div className="side_panel participants_panel">
          <div className="panel_header">
            <h3>Participantes ({activeParticipants.length})</h3>
            <button onClick={() => setShowParticipants(false)}>X</button>
          </div>
          <div className="panel_content">
            {activeParticipants.map((p) => (
              <div key={p.user._id} className="participant_item">
                <img src={p.user.picture || "/images/default_user.png"} alt="" />
                <div className="participant_info">
                  <span className="participant_name">
                    {p.user.first_name} {p.user.last_name}
                    {p.user._id === conference?.caller?._id && " (Host)"}
                    {p.user._id === user.id && " (Voce)"}
                  </span>
                </div>
                <div className="participant_status">
                  {!p.isAudioEnabled && <MuteMicIcon color="#e74c3c" />}
                  {!p.isVideoEnabled && <CameraOffIcon color="#e74c3c" />}
                </div>
                {isHost && p.user._id !== user.id && (
                  <button
                    className="remove_btn"
                    onClick={() => handleRemoveParticipant(p.user._id)}
                  >
                    Remover
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Waiting room panel */}
      {showWaitingRoom && isHost && (
        <div className="side_panel waiting_room_panel">
          <div className="panel_header">
            <h3>Sala de Espera ({waitingRoomUsers.length})</h3>
            <button onClick={() => setShowWaitingRoom(false)}>X</button>
          </div>
          <div className="panel_content">
            {waitingRoomUsers.map((w) => (
              <div key={w.user._id} className="participant_item">
                <img src={w.user.picture || "/images/default_user.png"} alt="" />
                <div className="participant_info">
                  <span className="participant_name">
                    {w.user.first_name} {w.user.last_name}
                  </span>
                </div>
                <div className="waiting_room_actions">
                  <button
                    className="admit_btn"
                    onClick={() => handleAdmit(w.user._id)}
                  >
                    Admitir
                  </button>
                  <button
                    className="deny_btn"
                    onClick={() => handleDeny(w.user._id)}
                  >
                    Negar
                  </button>
                </div>
              </div>
            ))}
            {waitingRoomUsers.length === 0 && (
              <p className="empty_message">Nenhum participante esperando</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
